import moment from 'moment';
import { Controller, useFormContext } from 'react-hook-form';

import { Button } from '@common/components/Button';
import { TextArea } from '@common/components/FormElements/Textarea';
import Portal from '@common/components/Portal';
import { StickyBarContent } from '@common/components/StickyBar/StickyBarContent';
import { useTranslations } from '@lang';

import { useGroupDiningOptionsByDateAndOptionGroup } from '@common/features/DynamicPackagesConfigurationWizard/FormSteps/hooks/useGroupOptionsByDate';
import { some } from '@utils/some';
import { ButtonWrapper } from '../../components/ExperienceDetails/ExperienceDetails.styles';
import { FormBox, TableFormContainer } from '../../components/TableForm/TableForm.styles';
import { TableVisitDateForm } from '../../components/TableVisitDateForm/TableVisitDateForm';
import { PackagesFormValues, TablesSchema } from '../../types';
import { PageTitle, StyledDateRangeIndicator, TablesDateContainer } from '../FormSteps.styles';
import { useHashNavigation } from '../hooks/useHashNavigation';
import { DiningOption } from './DiningOption/DiningOption';
import { TablesContainer, TablesWrapper } from './TablesStep.styles';
import { Fragment } from 'react';
import { usePackageConfiguration, useWizardNavigation, useCurrentStep } from '../../contexts';

export const TablesStep = () => {
  const { journeyParams, rate, room } = usePackageConfiguration();
  const { stickyBarId, handleNextStep, isLastStep, currentFormStep } = useWizardNavigation();
  const { packageTotalPrice, hash, isSubmitting } = useCurrentStep();

  useHashNavigation({ hash });

  const { watch, control } = useFormContext<PackagesFormValues, any>();
  const { t } = useTranslations();
  const tables = watch('tables');
  const isValid = TablesSchema.safeParse(tables).success;

  const currentStep = currentFormStep;
  const diningOptionsByDay = useGroupDiningOptionsByDateAndOptionGroup(
    moment(journeyParams.fromDate),
    moment(journeyParams.toDate),
    currentStep.diningOptions || []
  );

  return (
    <TablesWrapper>
      <TablesContainer>
        <PageTitle>{t('flynav.dining')}</PageTitle>
        <TableFormContainer>
          <FormBox>
            <TableVisitDateForm diningOptions={diningOptionsByDay} checkAllDays />
          </FormBox>
          {diningOptionsByDay.map(({ date, options }) =>
            some(options) ? (
              <Fragment key={date}>
                <TablesDateContainer>
                  <h2>{date}</h2>
                </TablesDateContainer>
                <FormBox>
                  {options.map((diningOption, index) => (
                    <DiningOption key={diningOption.id} option={diningOption} index={index} />
                  ))}
                </FormBox>
              </Fragment>
            ) : null
          )}

          <FormBox>
            <Controller
              name={`tables.notes`}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextArea
                  isFullWidth
                  labelWidth="150px"
                  name="note"
                  label={t('tables.notes')}
                  id="additional-notes"
                  onChange={(val) => {
                    if (val) {
                      onChange(val);
                    }
                  }}
                  input={{
                    value,
                  }}
                  error={''}
                />
              )}
            />
          </FormBox>
        </TableFormContainer>
        <Portal selector={`#${stickyBarId}`}>
          <StickyBarContent
            name={`${rate.title || ''} ${room.name ? ` | ${room.name}` : ''}`}
            subtitle={
              <StyledDateRangeIndicator
                fromDate={journeyParams.fromDate}
                toDate={journeyParams.toDate}
              />
            }
            displayedPrice={packageTotalPrice}
            button={
              <ButtonWrapper>
                <Button
                  isPrimary
                  type="button"
                  onClick={handleNextStep}
                  disabled={!isValid || isSubmitting}
                  isLoading={isSubmitting}
                >
                  {isLastStep ? t('product.ctaButton') : t('spa.package.nextStep')}
                </Button>
              </ButtonWrapper>
            }
          />
        </Portal>
      </TablesContainer>
    </TablesWrapper>
  );
};
